import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import SegmentedProgressBar from "../components/SegmentedProgressBar";
import FilePreviewModal from "../components/FilePreviewModal";
import ProfileEdit from "../components/ProfileEdit";
import { CheckCircle, Clock, FileText, ChevronDown, ChevronUp, Image } from "lucide-react";

export default function DosenDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mahasiswaList, setMahasiswaList] = useState([]);
  const [bimbingan, setBimbingan] = useState([]);
  const [expandedMhs, setExpandedMhs] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [mhsRes, bRes] = await Promise.all([
        axios.get(`${API}/users/mahasiswa`, { withCredentials: true }),
        axios.get(`${API}/bimbingan`, { withCredentials: true }),
      ]);
      setMahasiswaList(mhsRes.data);
      setBimbingan(bRes.data);
    } catch (_) {}
  };

  const handlePreviewFile = (fileUrl, fileName) => {
  setPreviewModal({
    blobUrl: fileUrl,
    fileName
    });
  };

  const closePreview = () => {
    if (previewModal?.blobUrl) URL.revokeObjectURL(previewModal.blobUrl);
    setPreviewModal(null);
  };

  const myStudents = mahasiswaList.filter(
    m => m.pembimbing1_id === user?.id || m.pembimbing2_id === user?.id
  );
  const getMhsBimbingan = (mhsId) => bimbingan.filter(b => b.mahasiswa_id === mhsId);
  const getMhsCount = (mhsId, dosenId) => bimbingan.filter(b => b.mahasiswa_id === mhsId && b.dosen_id === dosenId).length;
  const handlePrintLOA = (mahasiswaId) => {window.open(`${API}/loa/${mahasiswaId}?dosen_id=${user.id}`, "_blank");};
  
  function BimbinganRow({ b }) {
    const handlePrintLOA = (mahasiswaId) => {const token = localStorage.getItem("access_token"); window.open(`${API}/loa/${mahasiswaId}?token=${token}`, "_blank");};
    return (
      <div className="border border-slate-200 rounded-sm p-3 bg-white text-sm hover:shadow-sm transition-all">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#0F172A] truncate">{b.topik}</p>
            <p className="text-xs text-[#475569] mt-0.5">{b.tanggal}</p>
            <p className="text-xs text-[#475569] mt-0.5 line-clamp-2">{b.komentar}</p>
            {b.mahasiswa_name && <p className="text-xs text-[#002147] mt-1 font-medium">{b.mahasiswa_name}</p>}
          </div>
          {b.file_name && (
            <button onClick={() => handlePreviewFile(b.file_url, b.file_name)}
              data-testid={`preview-dosen-${b.id}`}
              className="flex-shrink-0 text-[#475569] hover:text-[#002147] p-1">
              <Image size={16} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex font-[Manrope]">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="ml-64 flex-1 p-8">
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Dashboard Dosen</h1>
              <p className="text-[#475569] mt-1">Selamat datang, {user?.name}</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Mahasiswa Bimbingan", value: myStudents.length, icon: <CheckCircle size={20} className="text-blue-500" /> },
                { label: "Total Bimbingan", value: bimbingan.length, icon: <FileText size={20} className="text-green-500" /> },
                { label: "Siap Ujian", value: myStudents.filter(m => getMhsCount(m.id, user?.id) >= 8).length, icon: <CheckCircle size={20} className="text-emerald-500" /> },
              ].map(s => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">{s.icon}<p className="text-xs uppercase tracking-widest text-[#475569]">{s.label}</p></div>
                  <p className="text-3xl font-bold text-[#002147] font-[Outfit]">{s.value}</p>
                </div>
              ))}
            </div>

            {bimbingan.slice(0, 5).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-4">Bimbingan Terbaru</h2>
                <div className="space-y-3">
                  {bimbingan.slice(0, 5).map(b => <BimbinganRow key={b.id} b={b} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "students" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Mahasiswa Saya</h1>
              <p className="text-[#475569] mt-1">{myStudents.length} mahasiswa bimbingan</p>
            </div>
            {myStudents.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-[#475569]">
                Belum ada mahasiswa yang terdaftar sebagai bimbingan Anda
              </div>
            ) : (
              <div className="space-y-4">
                {myStudents.map(mhs => {
                  const count = getMhsCount(mhs.id, user?.id);
                  
                  const isExpanded = expandedMhs === mhs.id;
                  const mhsBimbingan = getMhsBimbingan(mhs.id).filter(b => b.dosen_id === user?.id);
                  return (
                    <div key={mhs.id} data-testid={`mahasiswa-card-${mhs.id}`} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                          <h3 className="font-bold text-[#0F172A]">{mhs.name}</h3>

                          <div className="text-xs text-[#475569] mt-0.5 space-y-0.5">
                          <p>NIM: {mhs.nim}</p>
                          <p>{mhs.email}</p>
                          </div>

                          {mhs.judul_ta && (
                          <p className="text-xs text-[#002147] mt-1 font-medium">
                          {mhs.judul_ta}
                          </p>
                          )}
                        </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2.5 py-1 rounded-sm font-semibold ${count >= 8 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                              {count >= 8 ? "Siap Ujian" : "Belum Siap"}
                            </span>
                            {count >= 8 && (
                              <button onClick={() => handlePrintLOA(mhs.id)}
                              className="bg-[#002147] text-white text-xs px-3 py-1 rounded-sm hover:bg-[#00152e] transition-all">
                              Print LOA
                              </button>)
                            }
                            <button onClick={() => setExpandedMhs(isExpanded ? null : mhs.id)}
                              className="text-[#475569] hover:text-[#002147]">
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </div>
                        </div>
                        <SegmentedProgressBar value={count} label={`Bimbingan dengan ${user?.name}`} />
                      </div>
                      {isExpanded && (
                        <div className="border-t border-slate-100 p-5 bg-slate-50/50">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-3">Riwayat Bimbingan</h4>
                          {mhsBimbingan.length === 0 ? (
                            <p className="text-sm text-[#475569]">Belum ada bimbingan</p>
                          ) : (
                            <div className="space-y-2">
                              {mhsBimbingan.map(b => <BimbinganRow key={b.id} b={b} />)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "pending" && (
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-[#0F172A] font-[Outfit]">Semua Bimbingan</h1>
              <p className="text-[#475569] mt-1">{bimbingan.length} catatan bimbingan</p>
            </div>
            {bimbingan.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-[#475569]">
                <Clock size={32} className="mx-auto text-slate-400 mb-3" />
                <p>Belum ada catatan bimbingan</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bimbingan.map(b => <BimbinganRow key={b.id} b={b} />)}
              </div>
            )}
          </div>
        )}

        {/* Profil */}
        {activeTab === "profil" && (
          <ProfileEdit />
        )}
      </main>

      {previewModal && (
        <FilePreviewModal blobUrl={previewModal.blobUrl} fileName={previewModal.fileName} onClose={closePreview} />
      )}
    </div>
  );
}
